<script setup="">
import {
  AwardIcon,
  CircleCheckIcon,
  CoinsIcon,
  EyeIcon,
  PenIcon,
  PlusIcon,
  SettingsIcon,
  ShieldIcon,
  StarIcon,
  TicketIcon,
  UsersIcon
} from "lucide-vue-next";
import { computed, inject, ref } from "vue";

import NumberFormat from "@/Components/NumberFormat.vue";

import { Popover, PopoverButton, PopoverOverlay, PopoverPanel } from "@headlessui/vue";
import { Link, router, useForm, usePage } from "@inertiajs/vue3";

const emit = defineEmits(["update:open"]);

const props = defineProps({
  panelClass: {
    type: String,
    required: false
  },
  panelContentClass: {
    type: String,
    required: false
  }
});

const twMerge = inject("twMerge");
const page = usePage();
const form = useForm({});

const isLoading = ref(false);
const isOpen = ref(false);
const allTeams = ref([]);

const team = computed(() => page.props.team);
const computedPanelClass = twMerge("fixed overflow-hidden z-40 rounded-xl bg-white dark:bg-deep-900 shadow-soft", props.panelClass);
const computedPanelContentClass = twMerge("mx-auto max-w-campaign w-64", props.panelContentClass);

const switchTeam = (team) => {
  form.put(route("teams.switch", team), {
    preserveScroll: true,
    onSuccess: () => {
      router.visit(route("app.campaigns.index", team));
    }
  });
};

const fetchTeams = async (open) => {
  isOpen.value = !open;
  emit("update:open", !open);

  isLoading.value = true;
  const { data } = await axios.get(route("ajax.teams.index"));
  allTeams.value = data;
  isLoading.value = false;
};

const teamRingColor = (planName) => {
  switch (planName) {
    case "pro":
      return "ring-blue-700 dark:ring-blue-500";
    case "team":
      return "ring-purple-700 dark:ring-purple-500";
    default:
      return "ring-700";
  }
};

const teamTextColor = (planName) => {
  switch (planName) {
    case "pro":
      return "text-blue-700 dark:text-blue-500";
    case "team":
      return "text-purple-700 dark:text-purple-500";
    default:
      return "text-400";
  }
};
</script>

<template>
  <div class="team-profile">
    <Popover v-slot="{ open }" as="div">
      <PopoverButton class="w-full outline-0 focus:outline-none" @click="fetchTeams(open)">
        <slot :open="open" />
      </PopoverButton>

      <teleport to="body">
        <PopoverOverlay class="fixed inset-0 z-30 bg-black opacity-30 sm:hidden" />
      </teleport>

      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="translate-y-1 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="translate-y-1 opacity-0"
      >
        <PopoverPanel :class="computedPanelClass">
          <div :class="computedPanelContentClass">
            <div class="space-y-3 p-3 pb-4 text-sm">
              <div
                class="flex cursor-pointer items-center gap-x-3 rounded-lg p-1.5 transition-colors duration-200 hover:bg-50 dark:hover:bg-zinc-800/80"
              >
                <div class="relative flex-none">
                  <img
                    class="h-10 w-10 flex-none rounded-full object-cover shadow-sm ring-2 bg-50 dark:ring-zinc-500"
                    :class="teamRingColor(team.subscription.plan.name)"
                    :src="team.campaign.profile_photo_url"
                    alt="team profile image"
                  />
                  <div class="flex-center absolute -right-1 bottom-0 z-1 h-4 w-4 rounded-full text-white">
                    <span v-if="team.membership.role.key === 'owner'"><StarIcon class="h-4 w-4 fill-primary" /></span>
                    <span v-if="team.membership.role.key === 'admin'"><ShieldIcon class="h-4 w-4 fill-primary" /></span>
                    <span v-if="team.membership.role.key === 'editor'"><PenIcon class="h-4 w-4 fill-primary" /></span>
                    <span v-if="team.membership.role.key === 'guest'"><EyeIcon class="h-4 w-4 fill-primary" /></span>
                  </div>
                </div>
                <div class="w-full min-w-0">
                  <Link :href="route('teams.show', team)" class="block">
                    <p class="truncate text-sm+ leading-6 text-800" :class="{ 'font-semibold': team.hashid === team.hashid }">
                      {{ team.campaign.name }}
                    </p>
                  </Link>
                </div>
                <div>
                  <Link
                    :href="route('teams.show', team)"
                    class="flex rounded-full p-1.5 transition-colors duration-200 hover:bg-100 dark:hover:bg-zinc-700"
                  >
                    <SettingsIcon class="h-5 w-5 text-600 hover:text-primary" />
                  </Link>
                </div>
              </div>

              <ul
                class="divide-y overflow-hidden border-y text-sm border-100 divide-100 dark:divide-zinc-700 md:text-xs+"
                :class="teamTextColor(team.subscription.plan.name)"
              >
                <!-- 역할 정보 -->
                <li
                  class="group flex items-center justify-between px-4 py-2 transition-colors duration-200 hover:bg-50 dark:hover:bg-zinc-800/80 sm:px-2 sm:py-1.5"
                >
                  <div class="flex items-center space-x-3">
                    <AwardIcon class="h-4 w-4 text-600" />
                    <p class="text-xs+ text-700">역할</p>
                  </div>
                  <div class="font-semibold text-800">
                    <strong v-if="team.membership.role.key === 'owner'">소유자</strong>
                    <strong v-if="team.membership.role.key === 'admin'">관리자</strong>
                    <strong v-if="team.membership.role.key === 'editor'">편집자</strong>
                    <strong v-if="team.membership.role.key === 'guest'">게스트</strong>
                  </div>
                </li>

                <!-- 구독 정보 -->
                <li class="group px-4 py-2 transition-colors duration-200 hover:bg-50 dark:hover:bg-zinc-800/80 sm:px-2 sm:py-1.5">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3">
                      <TicketIcon class="h-4 w-4 text-600" />
                      <p class="text-xs+ text-700">구독</p>
                    </div>
                    <div class="flex items-center space-x-4 lg:space-x-2">
                      <div class="flex items-center gap-x-1 font-semibold">
                        <span v-if="team.subscription.plan.name === 'free'" class="text-700">무료</span>
                        <span v-if="team.subscription.plan.name === 'pro'" class="font-bold text-blue-700 dark:text-blue-500">프로</span>
                        <span v-if="team.subscription.plan.name === 'team'" class="font-bold text-purple-700 dark:text-purple-500">팀</span>
                        <span class="ml-0.5">플랜</span>
                      </div>
                      <Link
                        :href="route('teams.subscriptions.index', team)"
                        class="flex h-6 items-center justify-center rounded-full px-4 text-xs text-primary transition-colors text-700 bg-200 hover:bg-300 lg:px-2.5"
                      >
                        변경
                      </Link>
                    </div>
                  </div>
                </li>

                <!-- 팀원 정보 -->
                <li class="group px-4 py-2 transition-colors duration-200 hover:bg-50 dark:hover:bg-zinc-800/80 sm:px-2 sm:py-1.5">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3">
                      <UsersIcon class="h-4 w-4 text-600" />
                      <p class="text-xs+ text-700">팀원</p>
                    </div>
                    <div class="flex items-center space-x-4 lg:space-x-2">
                      <div class="font-semibold text-800">
                        <span>{{ team.member_count }}명</span>
                      </div>
                      <Link
                        :href="route('teams.members.index', team)"
                        class="flex h-6 items-center justify-center rounded-full px-4 text-xs text-primary transition-colors text-700 bg-200 hover:bg-300 lg:px-2.5"
                      >
                        설정
                      </Link>
                    </div>
                  </div>
                </li>

                <!-- 충전금 정보 -->
                <li class="group px-4 py-2 transition-colors duration-200 hover:bg-50 dark:hover:bg-zinc-800/80 sm:px-2 sm:py-1.5">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3">
                      <CoinsIcon class="h-4 w-4 text-600" />
                      <p class="text-xs+ text-700">충전금</p>
                    </div>
                    <div class="flex items-center space-x-4 lg:space-x-2">
                      <div class="font-semibold text-800" :class="{ 'text-primary': team.subscription.plan.name !== 'free' }">
                        <NumberFormat :value="team.wallet.balance.amount" />
                        원
                      </div>
                      <Link
                        :href="route('app.payments.wallets.index', team)"
                        class="flex h-6 items-center justify-center rounded-full px-4 text-xs text-primary transition-colors text-700 bg-200 hover:bg-300 lg:px-2.5"
                      >
                        충전
                      </Link>
                    </div>
                  </div>
                </li>

                <!-- 인증 정보 -->
                <li class="group px-4 py-2 transition-colors duration-200 hover:bg-50 dark:hover:bg-zinc-800/80 sm:px-2 sm:py-1.5">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3">
                      <ShieldIcon class="h-4 w-4 text-600" />
                      <p class="text-xs+ text-700">비즈니스</p>
                    </div>
                    <div class="flex items-center space-x-4 lg:space-x-2">
                      <div class="font-semibold">
                        <span v-if="team.is_verified" class="inline-flex items-center rounded-full bg-success px-2 py-0.5 text-xs text-white">
                          인증됨
                        </span>
                        <span v-else class="inline-flex items-center rounded-full bg-warning px-2 py-0.5 text-xs text-white"> 미인증 </span>
                      </div>
                      <Link
                        :href="route('teams.verification.index', team)"
                        class="flex h-6 items-center justify-center rounded-full px-4 text-xs text-primary transition-colors text-700 bg-200 hover:bg-300 lg:px-2.5"
                      >
                        {{ team.is_verified ? "확인" : "인증" }}
                      </Link>
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            <div class="text-sm">
              <div class="border-none pb-2 pt-2">
                <p class="px-3 text-xs+ font-semibold text-500">캠페인</p>
              </div>

              <ul role="list" class="max-h-[180px] divide-y overflow-y-auto border-y px-px scrollbar-hide">
                <template v-for="belongsTeam in allTeams" :key="belongsTeam.name">
                  <li
                    class="flex cursor-pointer items-center gap-x-3 px-3 py-2.5 transition-colors duration-200 hover:bg-50 dark:hover:bg-zinc-700/30"
                    :class="[team.hashid === belongsTeam.hashid ? 'bg-50/90 dark:bg-zinc-800/90' : '']"
                    @click="switchTeam(belongsTeam)"
                  >
                    <div class="relative flex-none">
                      <img
                        class="h-8 w-8 flex-none rounded-full object-cover ring-2 bg-50 dark:ring-zinc-500"
                        :class="teamRingColor(belongsTeam.subscription.plan)"
                        :src="belongsTeam.campaign.profile_photo_url"
                        alt="belongsTeam profile image"
                      />
                      <div class="flex-center absolute -right-1 bottom-0 z-1 h-4 w-4 rounded-full text-white">
                        <span v-if="belongsTeam.membership.role.key === 'owner'" v-tippy="{ content: '캠페인 소유자 입니다.' }">
                          <StarIcon class="h-4 w-4 fill-primary" />
                        </span>
                        <span v-if="belongsTeam.membership.role.key === 'admin'" v-tippy="{ content: '캠페인 관리자 입니다.' }">
                          <ShieldIcon class="h-4 w-4 fill-primary" />
                        </span>
                        <span v-if="belongsTeam.membership.role.key === 'editor'" v-tippy="{ content: '캠페인 편집자 입니다.' }">
                          <PenIcon class="h-4 w-4 fill-primary" />
                        </span>
                        <span v-if="belongsTeam.membership.role.key === 'guest'" v-tippy="{ content: '캠페인 게스트 입니다.' }">
                          <EyeIcon class="h-4 w-4 fill-primary" />
                        </span>
                      </div>
                    </div>
                    <div class="w-full min-w-0">
                      <p class="truncate leading-6 text-800" :class="{ 'font-semibold': team.hashid === belongsTeam.hashid }">
                        {{ belongsTeam.campaign.name }}
                      </p>
                    </div>
                    <div v-if="team.hashid === belongsTeam.hashid" class="w-6">
                      <CircleCheckIcon class="h-5 w-5 text-success" />
                    </div>
                  </li>
                </template>
                <li v-if="allTeams.length === 0" class="p-4 text-center text-400">
                  <p class="text-sm">새로운 캠페인을 생성해보세요!</p>
                </li>
              </ul>
              <!--end::team-list-->
            </div>

            <div class="py-4">
              <Link
                :href="route('teams.create')"
                class="flex-center group w-full rounded-md p-2 text-sm transition-colors duration-200 hover:bg-50 dark:hover:bg-zinc-800/80"
              >
                <PlusIcon class="mr-2 h-5 w-5 text-500 group-hover:text-primary" />
                <span class="text-700 group-hover:font-semibold group-hover:text-primary">새로운 캠페인 생성</span>
              </Link>
            </div>
            <!--end::create-team-->
          </div>
        </PopoverPanel>
      </transition>
    </Popover>
    <!--end::team-switcher-->
  </div>
</template>
